<script>
    import CopyLinkIcon from "@propublica/pp-v5-shared/src/icons/link.svg";
    import Button from "../Button";

    let textarea; // Binding for textarea

    let showTooltip = false;

    /** In most cases, this can be ignored. Set this to true if you’re showing an embed code or something else that would be useful to preview.
     * @type {boolean} */
    export let showTextarea = false;

    /** When you do want the textarea to be shown, you can pass it any arbitrary inline style.
     * (Use for changing the colors or to adjust the dimensions of the textarea.)
     * @type {string} */
    export let styleTextarea = null;

    /** The text to be copied to the clipboard. If you are instantiating CopyButton in SSR, then
     * you must specify the text to copy; otherwise, the CopyButton will not show up. If
     * you are only instantiating CopyButton in client JS, then it defaults to
     * the current page's URL, detected by JS (no need to specify this prop).
     * @type {string} */
    export let textToCopy =
        typeof window !== "undefined" ? window.location.href : null;

    /** @type {string} */
    export let text = "Copy";

    /** @type {string} */
    export let tooltipText = "Link Copied!";

    /** Pass along any props to the Button component here. If empty, it will
     * use the Button’s defaults.
     * @type {object}
     */
    export let buttonProps = null;

    function copyLinkToKeyboard() {
        textarea.select();

        try {
            document.execCommand("copy");
            showTooltip = true;
            setTimeout(() => {
                showTooltip = false;
            }, 2000);
        } catch (err) {
            console.error("Could not copy link to clipboard."); // eslint-disable-line no-console
        }
    }
</script>

{#if textToCopy !== null}
    <div class="copy-button-wrapper">
        <textarea
            bind:this={textarea}
            style={styleTextarea}
            class="copy-button-textarea"
            class:copy-button-textarea--hide={!showTextarea}
            readonly
            aria-hidden="true"
            tabindex="-1">{textToCopy}</textarea
        >

        <div class="copy-button-tooltip" class:-js-hide={!showTooltip}>
            <span>{tooltipText}</span>
        </div>

        <Button on:click={copyLinkToKeyboard} {...buttonProps}>
            <CopyLinkIcon slot="iconBefore" />
            {text}
        </Button>
    </div>
{/if}

<style lang="scss">
    .copy-button-wrapper {
        display: inline-flex;
        flex-direction: column;
        gap: var(--gap, 1rem);
        position: relative;
    }

    .copy-button-textarea {
        background-color: var(--gray-10);
        border: 0;
        font-size: var(--scale-1);
        line-height: 1.4;
    }

    .copy-button-textarea--hide {
        height: 0;
        width: 0;
        opacity: 0;
        position: absolute;
    }

    @keyframes tooltipPopIn {
        0% {
            opacity: 0;
            margin-top: 0;
            transform: translate(-50%, -100%) scale(0.9);
        }
        10% {
            opacity: 1;
            margin-top: calc(-1 * var(--spacing-1));
            transform: translate(-50%, -100%) scale(1);
        }
        90% {
            opacity: 1;
            margin-top: calc(-1 * var(--spacing-1));
            transform: translate(-50%, -100%) scale(1);
        }
        100% {
            opacity: 0;
            margin-top: 0;
            transform: translate(-50%, -100%) scale(0.9);
        }
    }

    .copy-button-tooltip {
        animation: tooltipPopIn ease-in-out 2s;
        animation-iteration-count: 1;
        animation-fill-mode: forwards;

        background-color: var(--black);
        border-radius: 4px;
        color: var(--white);
        font-family: var(--fonts-sans);
        font-size: var(--scale-2);
        font-weight: 700;
        padding: var(--spacing-1) var(--spacing0);
        text-align: center;
        white-space: nowrap;

        margin-left: 50%;
        margin-top: 0;
        position: absolute;
        transform: translate(-50%, -100%) scale(1);
        transform-origin: center center;
        z-index: 1000;
    }
</style>
